import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Drawer } from "components/ui/drawer/Drawer"
import { useSearchParams } from "react-router-dom"
import {
	useGetModelNumber,
	useGetModelNumberImageLink,
	useUpdateModelNumberCategories,
	useUpdateModelNumberPrices,
} from "hooks/api/modelNumbers/modelNumbers.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { H3 } from "components/ui/typography/titles/Titles"
import { Image } from "./layout/Image/Image"
import { Categories } from "./layout/Categories/Categories"
import { Dimensions } from "./layout/Dimensions/Dimensions"
import { Pricing } from "./layout/Pricing/Pricing"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { UnsavedChangesModal } from "./layout/UnsavedChangesModal/UnsavedChangesModal"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import type { TModelNumberPricePayloadItem } from "types/modelNumbers.type"
import style from "./DrawerModelNumber.module.scss"

const { drawer, drawerModelNumber, topBlock, content, left, right, skeleton, saveButton } = style

export const DrawerModelNumber = () => {
	const queryClient = useQueryClient()
	const { error, success } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: updateModelNumberCategories, isPending: isUpdateModelNumberCategoriesPending } =
		useUpdateModelNumberCategories()
	const { mutateAsync: updateModelNumberPrices, isPending: isUpdateModelNumberPricesPending } =
		useUpdateModelNumberPrices()
	const [searchParams, setSearchParams] = useSearchParams()
	const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false)
	const [isCategoriesDirty, setIsCategoriesDirty] = useState(false)
	const [isPricingDirty, setIsPricingDirty] = useState(false)
	const [hasSavablePricingChanges, setHasSavablePricingChanges] = useState(false)
	const [pendingRelatedCategoryId, setPendingRelatedCategoryId] = useState<number | null>(null)
	const [pendingPricingPayload, setPendingPricingPayload] = useState<TModelNumberPricePayloadItem[]>([])
	const [showPricingValidation, setShowPricingValidation] = useState(false)
	const modelNumberID = searchParams.get("modelNumberID")
	const modelNumberId = Number(modelNumberID)
	const { data: modelNumber, isPending: isModelNumberPending } = useGetModelNumber(modelNumberId)
	const { data: modelNumberImageData, error: modelNumberImageError } = useGetModelNumberImageLink(modelNumberId)

	const hasChanges = isCategoriesDirty || isPricingDirty
	const canSaveChanges = isCategoriesDirty || isPricingDirty

	useEffect(() => {
		setIsUnsavedChangesModalOpen(false)
		setIsCategoriesDirty(false)
		setIsPricingDirty(false)
		setHasSavablePricingChanges(false)
		setPendingRelatedCategoryId(null)
		setPendingPricingPayload([])
		setShowPricingValidation(false)
	}, [modelNumberID])

	const forceCloseDrawer = () => {
		const newParams = new URLSearchParams(searchParams)
		newParams.delete("modelNumberID")
		setSearchParams(newParams)
	}

	const closeHandle = () => {
		if (hasChanges) {
			setIsUnsavedChangesModalOpen(true)
			return
		}

		forceCloseDrawer()
	}

	const closeConfirmationModal = () => {
		setIsUnsavedChangesModalOpen(false)
	}

	const closeDrawerWithoutSaving = () => {
		setIsUnsavedChangesModalOpen(false)
		forceCloseDrawer()
	}

	const onSaveModelNumberHandle = async (closeAfterSave = false) => {
		if (!modelNumberId) return

		const shouldSaveCategories = isCategoriesDirty
		const shouldSavePricing = hasSavablePricingChanges && pendingPricingPayload.length > 0

		if (isPricingDirty && !hasSavablePricingChanges) {
			setShowPricingValidation(true)
			error("Complete all edited price values before saving")
			return
		}

		if (!shouldSaveCategories && !shouldSavePricing) {
			setIsUnsavedChangesModalOpen(false)
			return
		}

		let isCategoriesSaved = false
		let isPricingSaved = false

		if (shouldSaveCategories) {
			try {
				await updateModelNumberCategories({ id: modelNumberId, relatedCategoryId: pendingRelatedCategoryId })
				isCategoriesSaved = true
			} catch (e) {
				formatError(e)
				isCategoriesSaved = false
			}
		}

		if (shouldSavePricing) {
			try {
				await updateModelNumberPrices({ id: modelNumberId, prices: pendingPricingPayload })
				isPricingSaved = true
			} catch (e) {
				formatError(e)
				isPricingSaved = false
			}
		}

		if (isCategoriesSaved) {
			setIsCategoriesDirty(false)
		}

		if (isPricingSaved) {
			setIsPricingDirty(false)
			setHasSavablePricingChanges(false)
			setPendingPricingPayload([])
		}

		if (isCategoriesSaved || isPricingSaved) {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] }),
				queryClient.invalidateQueries({ queryKey: ["getModelNumber", modelNumberId] }),
			])
		}

		const allSavesSucceeded = (!shouldSaveCategories || isCategoriesSaved) && (!shouldSavePricing || isPricingSaved)

		if (allSavesSucceeded) {
			setIsUnsavedChangesModalOpen(false)

			if (closeAfterSave) {
				forceCloseDrawer()
			}

			success("Model number saved")
		}
	}

	return (
		<>
			<Drawer
				styles={{
					body: { backgroundColor: "var(--clr-superLightGray)", padding: 0 },
					content: { borderRadius: "16px", margin: "8px 0" },
				}}
				width={1256}
				onClose={closeHandle}
				open={!!modelNumberID}
				className={drawer}
			>
				{isModelNumberPending ? (
					<div className={skeleton}>
						<Skeleton rows={18} />
					</div>
				) : (
					<div className={drawerModelNumber}>
						<div className={topBlock}>
							<H3>Model: {modelNumber?.model_number}</H3>
							<button onClick={closeHandle}>
								<CloseIcon />
							</button>
						</div>
						<div className={content}>
							<div className={left}>
								<Image
									modelNumberId={modelNumberId}
									modelNumberImageLink={modelNumberImageData?.download_url}
									modelNumberImageError={modelNumberImageError}
								/>
								<Categories
									modelNumberId={modelNumberId}
									categoryHierarchy={modelNumber?.category_hierarchy}
									modelNumberName={modelNumber?.model_number}
									onDirtyChange={setIsCategoriesDirty}
									onRelatedCategoryChange={setPendingRelatedCategoryId}
								/>
								<Dimensions modelNumberId={modelNumberId} dimensions={modelNumber?.dimensions} />
							</div>
							<div className={right}>
								<Pricing
									pricingInfo={modelNumber?.pricing_info}
									onDirtyChange={setIsPricingDirty}
									onSavableChange={setHasSavablePricingChanges}
									onPayloadChange={setPendingPricingPayload}
									showValidation={showPricingValidation}
								/>
							</div>
						</div>
						{canSaveChanges && (
							<ButtonPrimary
								className={saveButton}
								onClick={() => onSaveModelNumberHandle()}
								submittable={!(isUpdateModelNumberCategoriesPending || isUpdateModelNumberPricesPending)}
							>
								Save changes
							</ButtonPrimary>
						)}
					</div>
				)}
			</Drawer>
			<UnsavedChangesModal
				isModalOpen={isUnsavedChangesModalOpen}
				onCancel={closeConfirmationModal}
				onCloseWithoutSaving={closeDrawerWithoutSaving}
				onSave={() => onSaveModelNumberHandle(true)}
				isSaving={isUpdateModelNumberCategoriesPending || isUpdateModelNumberPricesPending}
			/>
		</>
	)
}
